import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Modal from "@components/modals/Modal";
import { uploadFiles, fetchLanguages } from "@store/thunks/fileUploadAction";
import { closeModal } from "@store/slices/modalSlice";
import { fetchFoldersInfo } from "@store/thunks/dashboardThunk";

const FileUpload = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const comboboxRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [isMultiple, setIsMultiple] = useState(true);
  const [deadline, setDeadline] = useState("");
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingStep, setProcessingStep] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const processingSteps = {
    GA: [
      { message: "Analyzing file content...", duration: 1500 },
      { message: "Checking for duplicates...", duration: 2000 },
      { message: "Removing duplicate entries...", duration: 2500 },
      { message: "Optimizing data structure...", duration: 1500 },
      { message: "Splitting into chunks of 250 sentences...", duration: 2000 },
      { message: "Preparing final upload...", duration: 1500 }
    ],
    devnagri: [
      { message: "Analyzing file content...", duration: 1500 },
      { message: "Validating language compatibility...", duration: 2000 },
      { message: "Processing content structure...", duration: 2000 },
      { message: "Removing duplicate entries...", duration: 2500 },
      { message: "Splitting into chunks of 500 sentences...", duration: 2000 },
      { message: "Preparing final upload...", duration: 1500 }
    ]
  };

  const getActiveTab = () => {
    if (location.pathname.includes("/superAdmin/ga")) {
      return "GA";
    } else if (location.pathname.includes("/superAdmin/devnagri")) {
      return "devnagri";
    } else {
      return "GA";
    }
  };

  const activeTab = getActiveTab();
  const isOpen = useSelector((store) => store.modal.fileUpload);
  const { isUploading, error } = useSelector((state) => state.fileUpload);
  const { selectedFolderId } = useSelector((store) => store.dashboard);

  useEffect(() => {
    if (isOpen && activeTab === "devnagri") {
      const loadLanguages = async () => {
        setIsLoadingLanguages(true);
        try {
          const languageData = await dispatch(fetchLanguages());
          setLanguages(languageData || []);
        } catch (error) {
          console.error("Failed to load languages:", error);
        } finally {
          setIsLoadingLanguages(false);
        }
      };

      loadLanguages();
    }
  }, [isOpen, activeTab, dispatch]);

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setSelectedLanguage(null);
      setUploadStatus(null);
      setIsSubmitting(false);
      setSearchQuery("");
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Handle clicks outside the combobox to close the dropdown and clear input if necessary
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        if (selectedLanguage && searchQuery !== selectedLanguage.name) {
          setSearchQuery(selectedLanguage.name);
        } else if (!selectedLanguage) {
          setSearchQuery("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedLanguage, searchQuery]);

  const handleClose = () => {
    if (!isUploading && !isSubmitting) {
      setFiles([]);
      setUploadStatus(null);
      dispatch(closeModal("fileUpload"));
    }
  };

  const validateFileType = (file) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    return allowedTypes.includes(file.type);
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    if (!e.target.files || e.target.files.length === 0) {
      setFiles([]);
      return;
    }
    const newFiles = Array.from(e.target.files);
    const invalidFiles = newFiles.filter(file => !validateFileType(file));
    if (invalidFiles.length > 0) {
      setUploadStatus({
        type: "error",
        message: `Invalid file type(s). Only Excel (.xlsx) and CSV files are supported.`
      });
      return;
    }
    // Always keep only the latest file
setFiles([newFiles[newFiles.length - 1]]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    // Keep only the last dropped file
if (droppedFiles.length > 0) {
  setFiles([droppedFiles[droppedFiles.length - 1]]);
}

  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };


  const handleUpload = async () => {
  if (!selectedFolderId) {
    setUploadStatus({ type: "error", message: "No folder selected. Please select a folder first." });
    return;
  }
  if (files.length === 0) {
    setUploadStatus({ type: "error", message: "Please select at least one file to upload." });
    return;
  }
  if (activeTab === "devnagri" && !selectedLanguage) {
    setUploadStatus({ type: "error", message: "Please select a target language for Devnagri uploads." });
    return;
  }

  setIsSubmitting(true);
  setUploadStatus({ type: "info", message: "Uploading files..." });

  try {
    // start API request immediately
    const uploadPromise = dispatch(
      uploadFiles(
        activeTab,
        selectedLanguage ? selectedLanguage.languageId : null,
        files,
        selectedFolderId,
        deadline
      )
    );

    // show steps until upload resolves
    const steps = processingSteps[activeTab];
    let stopProcessing = false;

    uploadPromise.then(() => (stopProcessing = true)).catch(() => (stopProcessing = true));

    for (let i = 0; i < steps.length && !stopProcessing; i++) {
      setProcessingStep(steps[i].message);
      await new Promise(resolve => setTimeout(resolve, steps[i].duration));
      setProcessingProgress(((i + 1) / steps.length) * 100);
    }

    // now check result
    const result = await uploadPromise;

    if (result && result.success) {
      setUploadStatus({ type: "success", message: result.message || "Files uploaded successfully!" });
      setProcessingStep(null);
      setProcessingProgress(0);
      setFiles([]);
      dispatch(fetchFoldersInfo(activeTab, selectedFolderId));
      setTimeout(() => {
        dispatch(closeModal("fileUpload"));
      }, 1500);
    } else {
      setUploadStatus({
        type: "error",
        message: (result && result.error) || "Upload failed. Please try again."
      });
    }
  } catch (error) {
    console.error("Upload error:", error);
    setUploadStatus({ type: "error", message: error.error || "Upload failed. Please try again." });
  } finally {
    setIsSubmitting(false);
  }
};


  // Filter languages based on search query, excluding English
  const filteredLanguages = languages.filter(
    (lang) =>
      lang.name.toLowerCase() !== "english" &&
      lang.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle language selection
  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
    setSearchQuery(language ? language.name : "");
    setIsDropdownOpen(false);
  };

  // Toggle dropdown visibility and clear input if necessary
  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => {
      if (prev) {
        if (selectedLanguage && searchQuery !== selectedLanguage.name) {
          setSearchQuery(selectedLanguage.name);
        } else if (!selectedLanguage) {
          setSearchQuery("");
        }
      }
      return !prev;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${isMultiple ? "Multiple" : "Single"} File Upload`}
      maxWidth="md"
    >
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-lg font-semibold">Upload File</h4>
        </div>

        <div
          className={`relative border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 min-h-[200px] ${isUploading || isSubmitting
            ? "opacity-50 cursor-not-allowed bg-gray-50"
            : "border-[#FE6D1E] hover:border-[#E65A0C] hover:bg-orange-50/50"
            }`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !isUploading && !isSubmitting && fileInputRef.current.click()}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-orange-50/30 rounded-xl pointer-events-none"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="p-3 bg-orange-100/80 rounded-full mb-4">
              <svg
                className="h-10 w-10 text-[#FE6D1E]"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            <div className="space-y-3 text-center">
              <p className="text-xl font-semibold text-[#FE6D1E]">
                Upload Here
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept=".csv,.xlsx"
            multiple={false}
            disabled={isUploading || isSubmitting}
          />
        </div>

        <div className="mt-6 space-y-4">
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-[#FE6D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900">Source Language</h3>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Files will be translated from <span className="font-medium text-[#FE6D1E]">English</span>
            </p>
          </div>

          {activeTab === 'GA' ? null : (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-center space-x-2 mb-3">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <h3 className="text-sm font-semibold text-gray-900">Target Language</h3>
              </div>
              {isLoadingLanguages ? (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span>Loading available languages...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative" ref={comboboxRef}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsDropdownOpen(true);
                      }}
                      onFocus={() => setIsDropdownOpen(true)}
                      placeholder="Search and select target language"
                      className="w-full p-2 pr-10 bg-white border-2 border-blue-200 focus:border-blue-500 rounded-lg shadow-sm transition-colors duration-200 hover:border-blue-300"
                      disabled={isUploading || isSubmitting}
                    />
                    <div
                      className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                      onClick={toggleDropdown}
                    >
                      <svg
                        className={`w-5 h-5 text-blue-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {isDropdownOpen && (
                      <ul className="absolute z-10 w-full bg-white border border-blue-200 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg">
                        {(searchQuery ? filteredLanguages : languages.filter(lang => lang.name.toLowerCase() !== "english")).map((lang) => (
                          <li
                            key={lang.languageId}
                            onClick={() => handleLanguageSelect(lang)}
                            className="px-4 py-2 text-sm text-gray-900 hover:bg-blue-100 cursor-pointer transition-colors duration-150"
                          >
                            {lang.name}
                          </li>
                        ))}
                        {searchQuery && filteredLanguages.length === 0 && (
                          <div className="px-4 py-2 text-sm text-gray-600">
                            No languages found
                          </div>
                        )}
                      </ul>
                    )}
                  </div>
                  {!selectedLanguage && (
                    <p className="text-sm text-blue-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Please select the target language for translation
                    </p>
                  )}
                  {selectedLanguage && (
                    <p className="text-sm text-green-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Selected: {selectedLanguage.name}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {files.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h5 className="text-sm font-semibold text-gray-900">
                  Selected Files ({files.length})
                </h5>
              </div>
              <span className="text-sm font-medium text-[#FE6D1E] font-mono">
                {files.reduce((acc, file) => acc + file.size, 0) / 1024 > 1024
                  ? `${(files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024)).toFixed(2)} MB`
                  : `${(files.reduce((acc, file) => acc + file.size, 0) / 1024).toFixed(2)} KB`}
              </span>
            </div>
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <ul className="max-h-60 overflow-y-auto divide-y divide-gray-200">
                {files.map((file, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between p-3 hover:bg-gray-100 transition-colors duration-150"
                  >
                    <div className="flex items-center min-w-0">
                      <div className="bg-blue-100 rounded-lg p-2 mr-3">
                        <svg
                          className="h-5 w-5 text-blue-600"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-sm font-medium text-[#FE6D1E] font-mono">
                          {file.size > 1024 * 1024
                            ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                            : `${(file.size / 1024).toFixed(2)} KB`}
                        </p>
                      </div>
                    </div>
                    {!isUploading && !isSubmitting && (
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-4 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-200"
                        title="Remove file"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {isSubmitting && processingStep && (
          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">{processingStep}</span>
              <span className="text-sm font-semibold text-[#FE6D1E] font-mono">{Math.round(processingProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${processingProgress}%` }}
              ></div>
            </div>
            <div className="mt-2 flex justify-center">
              <div className="inline-flex items-center px-4 py-2 rounded-md bg-blue-50 text-blue-700">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing your files...
              </div>
            </div>
          </div>
        )}

        {uploadStatus && (
          <div className={`mt-4 p-3 rounded-md ${uploadStatus.type === "error" ? "bg-red-100 text-red-700" :
            uploadStatus.type === "success" ? "bg-green-100 text-green-700" :
              "bg-blue-100 text-blue-700"
            }`}>
            {uploadStatus.message}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleClose}
            className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            disabled={isUploading || isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md ${isUploading || isSubmitting || files.length === 0 || (activeTab === "devnagri" && !selectedLanguage)
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              }`}
            disabled={isUploading || isSubmitting || files.length === 0 || (activeTab === "devnagri" && !selectedLanguage)}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </span>
            ) : "Upload"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FileUpload;